import React, { useState } from 'react';
import I18n from 'i18n-js';

import { RCAlertBox, RCMenu, RCIconButton } from '@reverbdotcom/cadence/components';
import { DotsThreeBoldIcon } from '@reverbdotcom/cadence/icons/react';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { US, currencies } from '@reverbdotcom/commons/src/constants';
import {
  Listing,
  SellerListingCardPaymentSettingsQuery,
  core_apimessages_DirectCheckoutProfile_PayoutMethod,
} from '@reverbdotcom/commons/src/gql/graphql';

import EditButton from './footer_buttons/EditButton';
import RelistButton from './footer_buttons/RelistButton';
import { EndButton, EndModal } from './footer_buttons/EndButton';
import PublishButton from './footer_buttons/PublishButton';
import DeleteButton from './footer_buttons/DeleteButton';
import PaymentButton, { SELLER_LISTING_CARD_PAYMENT_SETTINGS_QUERY } from './footer_buttons/PaymentButton';
import SellerListingCardPaymentSettingsModal from './SellerListingCardPaymentSettingsModal';

const LIVE = 'live';
const SOLD = 'ordered';
const DRAFT = 'draft';

export const SELLER_SITE = 'seller_site';
interface IExternalProps {
  listing: Listing;
}

interface IDraftDeleteErrorsProps {
  draftDeleteErrors?: Array<string>;
  setDraftDeleteErrors: React.Dispatch<React.SetStateAction<Array<string>>>;
}

export function ListingActionButtons({ listing, setDraftDeleteErrors }: IExternalProps & IDraftDeleteErrorsProps) {
  const isEditable = listing.state !== SOLD || listing.relistable;

  const user = useUser();
  const walletOptInEnabled = useExpEnabled(experiments.LISTING_LEVEL_WALLET_OPT_IN);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
  const [isEndModalOpen, setIsEndModalOpen] = React.useState(false);
  const { data, loading } = useQuery<SellerListingCardPaymentSettingsQuery>(SELLER_LISTING_CARD_PAYMENT_SETTINGS_QUERY);

  const { myDirectCheckoutProfile } = data || {};
  const eligibleForWalletPayout = user.shopCountryCode === US && user.shopCurrency === currencies.USD;
  const isShopPayoutMethodWallet = myDirectCheckoutProfile?.payoutMethodEnum === core_apimessages_DirectCheckoutProfile_PayoutMethod.WALLET;
  const dcProfileComplete = !!myDirectCheckoutProfile?.accountNumberLastFour;
  const showPaymentButton = !loading && eligibleForWalletPayout && walletOptInEnabled && !isShopPayoutMethodWallet && dcProfileComplete;

  if (listing.state === DRAFT) {
    return (
      <RCMenu
        anchor={
          <RCIconButton
            size="mini"
            variant="muted"
            label="listing actions"
            svgComponent={DotsThreeBoldIcon}
          />
        }
      >
        <EditButton
          listing={listing}
        />
        <PublishButton
          listing={listing}
        />
        <DeleteButton
          listing={listing}
          setDraftDeleteErrors={setDraftDeleteErrors}
        />
      </RCMenu>
    );
  }

  const doNotRender = !isEditable && !showPaymentButton && !listing.relistable && !showPaymentButton && listing.state != LIVE;

  if (doNotRender) return null;

  return (
    <>
      <RCMenu
        anchor={
          <RCIconButton
            size="mini"
            variant="muted"
            label="listing actions"
            svgComponent={DotsThreeBoldIcon}
          />
        }
      >
        {isEditable && (
          <EditButton listing={listing} />
        )}
        {showPaymentButton && (
          <PaymentButton
            listing={listing}
            onClick={() =>  setIsPaymentModalOpen(true)}
          />
        )}
        {listing.relistable && (
          <RelistButton listing={listing} />
        )}
        {listing.state == LIVE && (
          <EndButton
            listing={listing}
            setModalOpen={setIsEndModalOpen}
          />
        )}
      </RCMenu>
      {showPaymentButton && (
        <SellerListingCardPaymentSettingsModal
          listing={listing}
          isModalOpen={isPaymentModalOpen}
          setIsModalOpen={setIsPaymentModalOpen}
        />
      )}
      {listing.state == LIVE && (
        <EndModal
          listing={listing}
          modalOpen={isEndModalOpen}
          setModalOpen={setIsEndModalOpen}
        />
      )}
    </>
  );
}

export function DeleteDraftListingErrors({
  draftDeleteErrors = [],
  setDraftDeleteErrors,
}: IDraftDeleteErrorsProps) {
  return (
    <div className="pb-8 pl-6 pr-4">
      <RCAlertBox
        type="error"
        onDismiss={() => {
          setDraftDeleteErrors([]);
        }}
      >
        {I18n.t(
          'discovery.listingsManagement.sellerListingCard.deleteDraftError',
        )}
        <ul className="ml-4">
          {draftDeleteErrors.map((errorMessage) => (
            <li key={errorMessage}>{errorMessage}</li>
          ))}
        </ul>
      </RCAlertBox>
    </div>
  );
}

export default function SellerListingCardActionMenu({ listing }: IExternalProps) {
  const [draftDeleteErrors, setDraftDeleteErrors] = useState([]);

  return (
    <>
      <ListingActionButtons
        listing={listing}
        setDraftDeleteErrors={setDraftDeleteErrors}
      />
      {draftDeleteErrors.length > 0 && (
        <DeleteDraftListingErrors
          draftDeleteErrors={draftDeleteErrors}
          setDraftDeleteErrors={setDraftDeleteErrors}
        />
      )}
    </>
  );
}
